import React, {Component, useState} from "react";
import { PrismPopover } from "@prism-ui/react";
import RatingSlider from './RatingSlider';
import "./CompetenciesStep.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { COMPETENCIES_MENTEE_INFO, COMPETENCIES_MENTOR_INFO, COMPETENCIES_COMMITTEE_MEMBER_INFO, COMPETENCIES_ADDITIONAL_SKILLS_PROMPT, RATING_COLORS, } from '../constants';
import {
    faChevronRight,
    faChevronLeft,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import ApplicationHeader from "../ApplicationHeader";
import SaveAndStepButton from "../SaveAndStepButton.jsx";
import MessageEditor from "../../MessageEditor/MessageEditor.tsx";



class CompetenciesStep extends Component {
    editorRef = React.createRef();

    constructor(props) {
        super(props);
        if (this.props.application.applicant_type_id === 1) {
            this.thisPageStep = 4;
        } else {
            this.thisPageStep = 5;
        }
    }

    onContinue = async () => {
        const applicationToSave = {
            ...this.state.application,
            additional_skills: this.state.additionalSkills, // Save only HTML content
          };
          await this.props.saveApplication(applicationToSave);
        this.props.jumpToStep(this.thisPageStep + 1);
    };

    onBack = async () => {
        const applicationToSave = {
            ...this.state.application,
            additional_skills: this.state.additionalSkills, // Save only HTML content
          };
          await this.props.saveApplication(applicationToSave);
        this.props.jumpToStep(this.thisPageStep - 1);
    };

    state = {
        info: "",
        application: this.props.application,

        formattedRatings: {},
        //css is configured manually to support 6 ratings as columns; if adjusted see css for gradients / column specifics
        validRatings: [
            "Very Weak",
            "Weak",
            "Slightly Weak",
            "Slightly Strong",
            "Strong",
            "Very Strong",
        ],
        hasChanges: false,
        disabled: true,
        hideContinue: true,
        exampleHeading: "Example(s) of Development Request",
        sliderValue: 1,
        additionalSkills: this.props.application.additional_skills,
        additionalSkillsWordCount: (() => {
            const html = this.props.application.additional_skills || '';
            const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
            return text.length === 0 ? 0 : text.split(' ').filter(w => w.length > 0).length;
        })(),
    };

    // default object for competency rating
    compRating = {
        competency: null,
        rating: "1",
        examples: null,
        application_id: this.props.application.id,
        hasChanges: false,
    };

    componentDidMount = async () => {
        try {
            // BUG FIX: Add try-catch to handle API failures
            await this.props.getCompetencies(this.props.application.applicant_type_id);
            await this.formatRatings();
            this.props.competencies.forEach(comp => {
                const compId = comp.id;
                const currentRating = this.state.formattedRatings[compId];
                if (!currentRating || !currentRating.rating || currentRating.rating === "0") {
                    this.handleRatingChange(compId, "1");
                }
            });
        } catch (error) {
            console.error('Failed to load competencies:', error);
        }
    };

    formatRatings = () => {
        return new Promise((resolve) => {
            let ratings = {};
            for (let index = 0; index < this.props.ratings.length; index++) {
                let id = this.props.ratings[index].competency;
                ratings[id] = this.props.ratings[index];
                delete ratings[id].created_at;
                delete ratings[id].updated_at;
            }
            this.setState({formattedRatings: ratings}, resolve);
        });
    };

    handleRatingChange = (compId, newValue) => {
        let newRatingAsString = String(newValue);

        this.setState(prevState => {
            let updatedRatings = {...prevState.formattedRatings};

            if (!updatedRatings[compId]) {
                updatedRatings[compId] = {...this.compRating, competency: compId};
            }

            updatedRatings[compId] = {
                ...updatedRatings[compId],
                rating: newRatingAsString
            };

            return {
                formattedRatings: updatedRatings,
                hasChanges: true
            };
        });
    };

    mapValueToLabel = (value) => {
      const index = value - 1;
      if (index >= 0 && index < this.state.validRatings.length) {
        return this.state.validRatings[index];
      }
      return "N/A";
    };

    handleSubmit = async (event) => {
        if (event) {
            event.preventDefault();
        }
        try {
            const applicationToSave = {
                ...this.state.application,
                additional_skills: this.state.additionalSkills,
            };
            await this.props.saveApplication(applicationToSave);
            await this.props.createCompetenciesRatings(Object.values(this.state.formattedRatings));
            this.setState({
                hasChanges: false,
                disabled: false,
                hideContinue: false,
            });
        } catch (error) {
            console.error("Error during competencies ratings submission:", error);
        }
    };
    handleAdditionalSkillsChange = (html) => {
        const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        const wordCount = text.length === 0 ? 0 : text.split(' ').filter(w => w.length > 0).length;
        this.setState({
            additionalSkills: html,
            additionalSkillsWordCount: wordCount,
            hasChanges: true,
        });
    };

    calculateRows(text) {
        if (text === null || text === undefined) {
            return 3;
        } else {
            const lines = text.split('\n');
            return lines.length > 3 ? lines.length : 3;
        }
    };

    createTable = () => {
        const sorted = [...this.props.competencies].sort((a, b) => a.name.localeCompare(b.name));
        let tableBody = sorted.map(comp => {
            let compId = comp.id;
            let compName = comp.name;
            let tooltipText = comp.tooltip_text;

            let rating = this.state.formattedRatings[compId]
                ? parseInt(this.state.formattedRatings[compId].rating, 10)
                : 1;

            return (
                <tr key={compId}>
                    <td>
                        {compName}
                        <PrismPopover as="context">
                            <button
                                slot="reference"
                                type="button"
                                className="info-trigger"
                                aria-label={`Show description for ${compName}`}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    size="lg"
                                />
                            </button>
                            <strong>{compName}</strong>
                            <p style={{ whiteSpace: 'pre-line', margin: '0.5rem 0 0' }}>{tooltipText}</p>
                        </PrismPopover>
                    </td>
                    <td colSpan="6" className="multi-span">
                        <div className="slider-container" style={{ '--rating-color': RATING_COLORS[rating - 1] || 'inherit' }}>
                            <RatingSlider
                                value={rating}
                                onChange={e => this.handleRatingChange(compId, e.target.value)}
                                min={1}
                                max={6}
                                tooltipLabel={this.mapValueToLabel}
                            />
                        </div>
                    </td>
                </tr>
            );
        });

        return (
            <table className="competency-table">
                <thead>
                <tr>
                    <th>Skill</th>
                    {this.state.validRatings.map((rating, index) => (
                        <th key={index}>
                          <span style={{
                            backgroundColor: RATING_COLORS[index],
                            // Fixed white text on the categorical rating swatch.
                            color: '#fff',
                            padding: '0.2rem 0.6rem',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                          }}>{rating}</span>
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>{tableBody}</tbody>
            </table>
        );
    };

    render() {
        const minRows = 3;
        const defaultRows = Math.max(this.calculateRows(this.state.application.additional_skills), minRows);

        let info = ""
        const applicantTypeId = Number(
          this.props.application?.ApplicantType?.id ||
          this.props.applicantTypeId ||
          this.props.application?.applicant_type_id
        );

        switch (applicantTypeId) {
          case 1:
            info = COMPETENCIES_MENTEE_INFO;
            break;
          case 2:
            info = COMPETENCIES_MENTOR_INFO;
            break;
          case 3:
            info = COMPETENCIES_COMMITTEE_MEMBER_INFO;
            break;
          default:
            info = '';
        }
        let additional_skills_prompt = COMPETENCIES_ADDITIONAL_SKILLS_PROMPT;


        return (
            <div>
                <ApplicationHeader message={info}/>

                <form onSubmit={this.handleSubmit}>
                    {this.createTable()}
                    <div>
                        <p style={{
                              fontSize: '1.05rem',
                              textAlign: 'center',
                              margin: '1em auto',
                              marginBottom: '1em',
                              marginTop: '2.5em',
                              maxWidth: '36em'
                            }}>
                              {additional_skills_prompt}
                            </p>
                            <MessageEditor 
                                ref={this.editorRef}
                                value={this.state.additionalSkills}
                                onChange={this.handleAdditionalSkillsChange}
                            />
                            <div style={{
                                textAlign: 'right',
                                fontSize: '0.85rem',
                                marginTop: '0.4rem',
                                color: 'var(--color-text-faded)',
                            }}>
                                {this.state.additionalSkillsWordCount || 0} words
                            </div>
                    </div>
                    <div className="nav-btns">
                        <SaveAndStepButton
                            buttonText="Back"
                            icon={faChevronLeft}
                            iconPosition="left"
                            componentsToCheck={[]}
                            onSave={this.handleSubmit}
                            onSuccessFunction={this.onBack}
                        />
                        <SaveAndStepButton
                            buttonText="Continue"
                            icon={faChevronRight}
                            iconPosition="right"
                            componentsToCheck={[]}
                            onSave={this.handleSubmit}
                            onSuccessFunction={this.onContinue}
                        />
                    </div>
                </form>
            </div>
        );
    }
}

export default CompetenciesStep;